/* eslint-disable */

import type { Address } from "viem"
import type { Config } from "wagmi"
import { formatUnits, isAddressEqual } from "viem"

import type { ApprovalMode, EmitEvent, Token } from "@/domains/core"
import { EventEmitter } from "@/domains/library"
import { approveOwnerSpenderAllowance, createLiquidityPool, TransactionReturnType } from "@/domains/on-chain"

export type CreateLiquidityPoolResult = {
  token0: { wei: bigint; value: string }
  token1: { wei: bigint; value: string }
  routerAllowanceToken0: { wei: bigint; value: string }
  routerAllowanceToken1: { wei: bigint; value: string }
  pairExists: boolean
  pairExistsAddress?: Address
  newPairSymbol: string
}
interface CreateLiquidityPoolParams {
  account: Address
  chainId: number
  config: Config
  nativeToken: Token
  wrappedToken: Token
  token0: Token
  token1: Token
  token0AmountBI: bigint
  token1AmountBI: bigint
  token0AmountMinBI: bigint
  token1AmountMinBI: bigint
  isStable: boolean
  deadline: number
  routerAddress: Address
  emitter: EventEmitter<EmitEvent>
  lens: CreateLiquidityPoolResult
  approvalMode: ApprovalMode
  onCreatePoolBegin: () => void
  onCreatePoolSuccess: (result: TransactionReturnType) => void
}

export const createLiquidityPoolInteraction = async ({
  account,
  chainId,
  config,
  nativeToken,
  token0,
  token1,
  wrappedToken,
  token0AmountBI,
  token1AmountBI,
  token0AmountMinBI,
  token1AmountMinBI,
  isStable,
  routerAddress,
  deadline,
  emitter,
  lens,
  approvalMode,
  onCreatePoolBegin,
  onCreatePoolSuccess,
}: CreateLiquidityPoolParams) => {
  if (
    token0.symbol === nativeToken.symbol ||
    token1.symbol === nativeToken.symbol ||
    isAddressEqual(token0.address, nativeToken.address) ||
    isAddressEqual(token1.address, nativeToken.address)
  ) {
    emitter.emit("create-liquidity-pool", {
      title: `Invalid token`,
      description: `You cannot create a pool with ${nativeToken.symbol}. Please use ${wrappedToken.symbol} instead`,
      variant: "error",
    })

    return
  }

  if (lens.pairExists) {
    emitter.emit("create-liquidity-pool", {
      title: `Pair already exists`,
      description: `The pair ${lens.newPairSymbol} already exists`,
      variant: "error",
    })

    return
  }

  // Token 0 Balance & Check
  if (lens.token0.wei < token0AmountBI) {
    emitter.emit("create-liquidity-pool", {
      title: "Insufficient balance",
      description: `You have ${lens.token0.value} ${token0.symbol} but you are trying to spend ${formatUnits(token0AmountBI, token0.decimals)} ${token0.symbol}`,
      variant: "error",
    })
    return
  }

  // Token 1 Balance & Check
  if (lens.token1.wei < token1AmountBI) {
    emitter.emit("create-liquidity-pool", {
      title: "Insufficient balance",
      description: `You have ${lens.token1.value} ${token1.symbol} but you are trying to spend ${formatUnits(token1AmountBI, token1.decimals)} ${token1.symbol}`,
      variant: "error",
    })
    return
  }

  // Token 0 Approval
  if (lens.routerAllowanceToken0.wei < token0AmountBI) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${formatUnits(token0AmountBI, token0.decimals)} ${token0.symbol}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account,
      chainId,
      config,
      contractAddress: token0.address,
      spenderAddress: routerAddress,
      spenderAmount: token0AmountBI,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", { title: "Approval successful", variant: "success" })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error,
        variant: "error",
      })
      return
    }
  }

  // Token 1 Approval
  if (lens.routerAllowanceToken1.wei < token1AmountBI) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${formatUnits(token1AmountBI, token1.decimals)} ${token1.symbol}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account,
      chainId,
      config,
      contractAddress: token1.address,
      spenderAddress: routerAddress,
      spenderAmount: token1AmountBI,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", { title: "Approval successful", variant: "success" })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error,
        variant: "error",
      })
      return
    }
  }

  // Allow allowance state to propagate before addLiquidity (avoids safeTransferFrom failed on fast chains)
  const didApprove = lens.routerAllowanceToken0.wei < token0AmountBI || lens.routerAllowanceToken1.wei < token1AmountBI
  if (didApprove) {
    await new Promise((resolve) => setTimeout(resolve, 2000))
  }

  onCreatePoolBegin?.()
  const result = await createLiquidityPool({
    account,
    chainId,
    config,
    token0,
    token1,
    isStable,
    amountADesired: token0AmountBI, // naming convention switch
    amountBDesired: token1AmountBI,
    amountAMin: token0AmountMinBI,
    amountBMin: token1AmountMinBI,
    deadline,
    routerAddress,
  })

  if (result.success) {
    emitter.emit("create-liquidity-pool", { title: "Create pool successful", variant: "success" })
    console.log("createLiquidityPool->success->result", result)
    onCreatePoolSuccess?.(result)
    return result
  }

  if (result.success === false) {
    emitter.emit("create-liquidity-pool", {
      title: "Unable to add create liquidity pool",
      description: result.error,
      variant: "error",
    })
  }

  return result
}
